import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Grid,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  LoginOutlined,
  LogoutOutlined,
  HistoryOutlined,
  MyLocationOutlined,
  RefreshOutlined,
  TodayOutlined,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/Api';
import {
  formatDate,
  formatTime,
  getApiErrorMessage,
  getBrowserDeviceId,
  getBrowserLocation,
  getSchoolId,
  getStoredTeacher,
  teacherPortalGridItemSx,
  teacherPortalPanelCardSx,
  teacherPortalRowSx,
} from './teacherPortalUtils';

const statusColor = (status) => {
  if (status === 'On Time') return 'success';
  if (status === 'Late') return 'warning';
  if (status === 'Holiday') return 'info';
  if (status === 'Absent') return 'error';
  return 'default';
};

export default function TeacherClockScreen() {
  const theme = useTheme();
  const teacher = getStoredTeacher();
  const schoolId = getSchoolId(teacher);
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clocking, setClocking] = useState('');
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [todayRes, historyRes] = await Promise.all([
        api.get('/attendance/teacher/today'),
        api.get('/attendance/teacher/daily-records'),
      ]);

      setToday(todayRes.data?.data || null);
      setHistory(historyRes.data?.data || []);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load teacher attendance.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClock = async (type) => {
    setClocking(type);
    setError('');
    try {
      const position = await getBrowserLocation();
      const { latitude, longitude, accuracy } = position.coords;
      let termId = teacher?.currentTerm?._id || today?.termId || teacher?.termId;

      if (!termId && schoolId) {
        const termsRes = await api.get('/term/all', { params: { school: schoolId } });
        const terms = termsRes.data?.allTerms || termsRes.data?.terms || [];
        const now = new Date();
        const currentTerm =
          terms.find((term) => {
            if (!term.startDate || !term.endDate) return term.isCurrent === true;
            return now >= new Date(term.startDate) && now <= new Date(term.endDate);
          }) || terms[0];
        termId = currentTerm?._id;
      }

      const payload = {
        type,
        teacherId: teacher.teacherId,
        date: new Date().toISOString(),
        timestamp: new Date().toISOString(),
        termId,
        deviceUUID: getBrowserDeviceId(),
        latitude,
        longitude,
        rawLatitude: latitude,
        rawLongitude: longitude,
        accuracy,
        weakSignalAssist: false,
      };

      if (!termId) {
        throw new Error('Current term is missing. Ask an admin to set the active term.');
      }

      const res = await api.post('/attendance/teacher/clock', payload);
      toast.success(res.data?.data?.message || 'Attendance recorded.');
      await fetchData();
    } catch (err) {
      setError(getApiErrorMessage(err, err.message || 'Clock action failed.'));
    } finally {
      setClocking('');
    }
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight={900}>
            Clock In / Clock Out
          </Typography>
          <Typography color="text.secondary">
            Uses browser GPS to verify teacher attendance.
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshOutlined />}
          onClick={fetchData}
          variant="outlined"
          disabled={loading}
          sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, textTransform: 'none' }}
        >
          Refresh
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={5} sx={teacherPortalGridItemSx}>
          <Card sx={teacherPortalPanelCardSx(theme)}>
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <TodayOutlined fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={800}>
                        Today
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Current clock status
                      </Typography>
                    </Box>
                  </Stack>
                  <Chip
                    label={today?.status || 'Loading'}
                    color={statusColor(today?.status)}
                    variant="outlined"
                  />
                </Stack>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <MyLocationOutlined color="primary" />
                      <Typography color="text.secondary">
                        Location will be requested only when you tap a clock button.
                      </Typography>
                    </Stack>

                    <Stack spacing={1}>
                      <Typography>
                        Clocked in: <strong>{today?.clockedIn ? 'Yes' : 'No'}</strong>
                      </Typography>
                      <Typography>
                        Clocked out: <strong>{today?.clockedOut ? 'Yes' : 'No'}</strong>
                      </Typography>
                      <Typography>
                        Last action: <strong>{formatTime(today?.lastAction)}</strong>
                      </Typography>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={clocking === 'in' ? <CircularProgress size={18} color="inherit" /> : <LoginOutlined />}
                        disabled={!today?.canClockIn || Boolean(clocking)}
                        onClick={() => handleClock('in')}
                        sx={{ textTransform: 'none', borderRadius: 1 }}
                      >
                        Clock In
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={clocking === 'out' ? <CircularProgress size={18} /> : <LogoutOutlined />}
                        disabled={!today?.canClockOut || Boolean(clocking)}
                        onClick={() => handleClock('out')}
                        sx={{ textTransform: 'none', borderRadius: 1 }}
                      >
                        Clock Out
                      </Button>
                    </Stack>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7} sx={teacherPortalGridItemSx}>
          <Card sx={teacherPortalPanelCardSx(theme)}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <HistoryOutlined fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={800}>
                    Recent Attendance
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Latest teacher attendance records
                  </Typography>
                </Box>
              </Stack>
              <Stack spacing={1.25}>
                {history.slice(0, 10).map((record) => (
                  <Box
                    key={record._id}
                    sx={teacherPortalRowSx(theme, {
                      display: 'flex',
                      gap: 2,
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                    })}
                  >
                    <Box>
                      <Typography fontWeight={800}>{formatDate(record.date)}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        In: {formatTime(record.signInTime)} • Out: {formatTime(record.signOutTime)}
                      </Typography>
                    </Box>
                    <Chip label={record.status || 'N/A'} color={statusColor(record.status)} size="small" />
                  </Box>
                ))}
                {!loading && history.length === 0 && (
                  <Typography color="text.secondary">No attendance records yet.</Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
